import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Paper, Stack, CircularProgress,
  Card, CardContent, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, LinearProgress,
} from '@mui/material';
import {
  Assignment as CaseIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  TrendingUp as TatIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { bgvApi } from '../../../api/bgvApi';
import { BgvDashboardDto, BgvCaseDto, BgvCaseStatus, BgvFinalDecision } from '../../../types/bgvTypes';

const buildDashboardFromCases = (cases: BgvCaseDto[]): BgvDashboardDto => {
  const totalCases = cases.length;
  const pendingCases = cases.filter((caseItem) => caseItem.status === BgvCaseStatus.Pending).length;
  const inProgressCases = cases.filter((caseItem) => caseItem.status === BgvCaseStatus.InProgress).length;
  const completedCases = cases.filter((caseItem) => caseItem.status === BgvCaseStatus.Completed).length;
  const failedCases = cases.filter((caseItem) => caseItem.status === BgvCaseStatus.Failed).length;

  const clearCases = cases.filter((caseItem) => caseItem.finalDecision === BgvFinalDecision.Clear).length;
  const concernCases = cases.filter((caseItem) => caseItem.finalDecision === BgvFinalDecision.Concern).length;
  const notClearCases = cases.filter((caseItem) => caseItem.finalDecision === BgvFinalDecision.NotClear).length;

  const vendorMap = new Map<string, { vendorId: string; vendorName: string; totalTasks: number; completedTasks: number }>();
  cases.forEach((caseItem) => {
    if (!caseItem.vendorId || !caseItem.vendorName) return;

    const existing = vendorMap.get(caseItem.vendorId);
    if (!existing) {
      vendorMap.set(caseItem.vendorId, {
        vendorId: caseItem.vendorId,
        vendorName: caseItem.vendorName,
        totalTasks: caseItem.totalTasks,
        completedTasks: caseItem.completedTasks,
      });
      return;
    }

    existing.totalTasks += caseItem.totalTasks;
    existing.completedTasks += caseItem.completedTasks;
  });

  return {
    totalCases,
    pendingCases,
    inProgressCases,
    completedCases,
    failedCases,
    clearCases,
    concernCases,
    notClearCases,
    averageTatDays: 0,
    vendorPerformance: Array.from(vendorMap.values()).map((vendorPerformance) => ({
      ...vendorPerformance,
      averageTatDays: 0,
    })),
  };
};

const BgvDashboardPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<BgvDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const data = await bgvApi.getDashboard();
        setDashboard(data);
      } catch {
        try {
          const cases = await bgvApi.getCases();
          setDashboard(buildDashboardFromCases(cases));
          toast.warning('Dashboard service is unavailable. Showing summary from cases.');
        } catch {
          toast.error('Failed to load BGV dashboard');
        }
      } finally {
        setLoading(false);
      }
    };
    void loadDashboard();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboard) return null;

  const summaryCards = [
    { label: 'Total Cases', value: dashboard.totalCases, icon: <CaseIcon />, color: '#667eea' },
    { label: 'Pending', value: dashboard.pendingCases, icon: <PendingIcon />, color: '#ffa726' },
    { label: 'In Progress', value: dashboard.inProgressCases, icon: <CaseIcon />, color: '#29b6f6' },
    { label: 'Completed', value: dashboard.completedCases, icon: <CompletedIcon />, color: '#66bb6a' },
    { label: 'Failed', value: dashboard.failedCases, icon: <FailedIcon />, color: '#ef5350' },
    { label: 'Avg TAT (days)', value: dashboard.averageTatDays, icon: <TatIcon />, color: '#ab47bc' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>Background Verification Dashboard</Typography>

      <Grid container spacing={2} mb={4}>
        {summaryCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 2 }} key={card.label}>
            <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Final Decision Summary */}
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Final Decisions</Typography>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Clear</Typography>
                <Chip label={dashboard.clearCases} color="success" size="small" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Concern</Typography>
                <Chip label={dashboard.concernCases} color="warning" size="small" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Not Clear</Typography>
                <Chip label={dashboard.notClearCases} color="error" size="small" />
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Vendor Performance */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Vendor Performance</Typography>
            {dashboard.vendorPerformance.length === 0 ? (
              <Typography color="text.secondary">No vendor data available</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="center">Total Tasks</TableCell>
                      <TableCell align="center">Completed</TableCell>
                      <TableCell align="center">Progress</TableCell>
                      <TableCell align="center">Avg TAT (days)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboard.vendorPerformance.map((v) => (
                      <TableRow key={v.vendorId}>
                        <TableCell>{v.vendorName}</TableCell>
                        <TableCell align="center">{v.totalTasks}</TableCell>
                        <TableCell align="center">{v.completedTasks}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 120 }}>
                          <LinearProgress
                            variant="determinate"
                            value={v.totalTasks > 0 ? (v.completedTasks / v.totalTasks) * 100 : 0}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </TableCell>
                        <TableCell align="center">{v.averageTatDays}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BgvDashboardPage;
